// frontend/src/hooks/useInactivityLogout.js
// Cierra sesión automáticamente tras X minutos de inactividad (estándar AEMPS).
import { useEffect, useRef } from 'react';

const INACTIVITY_MINUTES = 30; // estándar farmacéutico
const WARNING_MINUTES = 1;     // aviso 1 minuto antes
const INACTIVITY_MS = INACTIVITY_MINUTES * 60 * 1000;
const WARNING_MS = (INACTIVITY_MINUTES - WARNING_MINUTES) * 60 * 1000;

/**
 * Hook que cierra sesión automáticamente tras 30 minutos de inactividad.
 * Avisa al usuario 1 minuto antes con confirm() para que pueda mantener la sesión.
 *
 * @param {function} onLogout - función a llamar para cerrar sesión
 * @param {boolean} enabled - si está activo (típicamente: usuario logueado)
 */
export default function useInactivityLogout(onLogout, enabled = true) {
  const timerRef = useRef(null);
  const warningTimerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    const resetTimers = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);

      // Aviso 1 minuto antes del cierre
      warningTimerRef.current = setTimeout(() => {
        const continuar = window.confirm(
          '⚠️ Por seguridad, tu sesión se cerrará en 1 minuto por inactividad.\n\n' +
          '¿Quieres continuar trabajando?'
        );
        if (continuar) {
          resetTimers(); // reinicia los temporizadores
        }
      }, WARNING_MS);

      // Cierre definitivo a los 30 minutos
      timerRef.current = setTimeout(() => {
        alert('Sesión cerrada por inactividad (30 minutos).');
        onLogout();
      }, INACTIVITY_MS);
    };

    // Eventos que cuentan como "actividad"
    const eventos = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    eventos.forEach(e => window.addEventListener(e, resetTimers, { passive: true }));

    // Iniciar el primer temporizador
    resetTimers();

    return () => {
      eventos.forEach(e => window.removeEventListener(e, resetTimers));
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    };
  }, [onLogout, enabled]);
}
